import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { AccessTime } from '@mui/icons-material';
import { appointmentService } from '../services/appointmentService';

interface TimeSlot {
  id: string;
  startTime: string;
  endTime: string;
  isAvailable: boolean;
}

interface AppointmentCalendarProps {
  caretakerId: string;
  selectedDate: string;
  onDateSelect: (date: string) => void;
  onTimeSlotSelect: (timeSlot: TimeSlot) => void;
  onError: (error: string) => void;
}

const AppointmentCalendar: React.FC<AppointmentCalendarProps> = ({
  caretakerId,
  selectedDate,
  onDateSelect,
  onTimeSlotSelect,
  onError,
}) => {
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<TimeSlot | null>(null);
  const [selectedDateValue, setSelectedDateValue] = useState<Date | null>(null);

  const fetchAvailableSlots = useCallback(async (date: string) => {
    try {
      setLoading(true);
      const response = await appointmentService.getAvailableSlots(caretakerId, date);
      setAvailableSlots(response.availableSlots || []);
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to load available time slots');
      setAvailableSlots([]);
    } finally {
      setLoading(false);
    }
  }, [caretakerId, onError]);

  // Set initial date to today
  useEffect(() => {
    if (!selectedDate) {
      const today = new Date();
      setSelectedDateValue(today);
      onDateSelect(today.toISOString().split('T')[0]);
    } else {
      setSelectedDateValue(new Date(selectedDate));
    }
  }, [selectedDate, onDateSelect]);

  // Fetch available slots when date changes
  useEffect(() => {
    if (selectedDate && caretakerId) {
      fetchAvailableSlots(selectedDate);
    }
  }, [selectedDate, caretakerId, fetchAvailableSlots]);

  const handleDateChange = (date: Date | null) => {
    if (date) {
      const dateString = date.toISOString().split('T')[0];
      setSelectedDateValue(date);
      onDateSelect(dateString);
      setSelectedTimeSlot(null);
    }
  };

  const handleTimeSlotSelect = (timeSlot: TimeSlot) => {
    setSelectedTimeSlot(timeSlot);
    onTimeSlotSelect(timeSlot);
  };

  const formatTime = (timeString: string) => {
    const [hours, minutes] = timeString.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  };

  const formatTimeRange = (startTime: string, endTime: string) => {
    return `${formatTime(startTime)} - ${formatTime(endTime)}`;
  };

  const isDateDisabled = (date: Date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const getNextAvailableDates = () => {
    const dates = [];
    const today = new Date();
    for (let i = 0; i < 30; i++) {
      const date = new Date(today);
      date.setDate(today.getDate() + i);
      dates.push(date);
    }
    return dates;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Choose Date & Time
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Select your preferred date and available time slot.
      </Typography>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle1" gutterBottom>
              Select Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                value={selectedDateValue}
                onChange={handleDateChange}
                shouldDisableDate={isDateDisabled}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    variant: 'outlined',
                  },
                }}
              />
            </LocalizationProvider>
          </Box>

          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Quick Date Selection
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {getNextAvailableDates().slice(0, 7).map((date) => {
                const dateString = date.toISOString().split('T')[0];
                const isSelected = dateString === selectedDate;
                const isToday = date.toDateString() === new Date().toDateString();
                
                return (
                  <Button
                    key={dateString}
                    variant={isSelected ? 'contained' : 'outlined'}
                    size="small"
                    onClick={() => handleDateChange(date)}
                    sx={{ minWidth: 'auto' }}
                  >
                    {isToday ? 'Today' : date.toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </Button>
                );
              })}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom>
            Available Time Slots
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : availableSlots.length === 0 ? (
            <Alert severity="info">
              No available time slots for the selected date. Please choose a different date.
            </Alert>
          ) : (
            <Box sx={{ maxHeight: 400, overflowY: 'auto' }}>
              <Grid container spacing={1}>
                {availableSlots.map((slot) => (
                  <Grid item xs={12} sm={6} key={slot.id}>
                    <Card
                      sx={{
                        cursor: 'pointer',
                        border: selectedTimeSlot?.id === slot.id ? 2 : 1,
                        borderColor: selectedTimeSlot?.id === slot.id ? 'primary.main' : 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1,
                        },
                      }}
                      onClick={() => handleTimeSlotSelect(slot)}
                    >
                      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AccessTime sx={{ fontSize: 16, color: 'text.secondary' }} />
                          <Typography variant="body2" fontWeight="medium">
                            {formatTimeRange(slot.startTime, slot.endTime)}
                          </Typography>
                        </Box>
                        {selectedTimeSlot?.id === slot.id && (
                          <Chip
                            label="Selected"
                            size="small"
                            color="primary"
                            sx={{ mt: 1 }}
                          />
                        )}
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </Grid>
      </Grid>

      {selectedTimeSlot && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1 }}>
          <Typography variant="subtitle2" color="primary">
            Selected Time Slot
          </Typography>
          <Typography variant="body2">
            {selectedDateValue?.toLocaleDateString('en-US', { 
              weekday: 'long', 
              year: 'numeric', 
              month: 'long', 
              day: 'numeric' 
            })} at {formatTimeRange(selectedTimeSlot.startTime, selectedTimeSlot.endTime)}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AppointmentCalendar;
